import type { Job, JobKind } from '@zyx/shared';
import { ChevronDown, ChevronRight, Pause, Play, RotateCcw, Square } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingRows } from '@/components/common/states';
import { Button } from '@/components/ui/button';
import { type JobAction, useDeadItems, useJobAction, useJobs } from '@/hooks/use-indexing';
import { formatAgo } from '@/lib/format';
import { urls } from '@/lib/urls';
import { cn } from '@/lib/utils';

const STATUS_STYLES: Readonly<Record<Job['status'], string>> = {
  pending: 'bg-muted text-muted-foreground',
  running: 'bg-primary/15 text-primary',
  paused: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  completed: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  failed: 'bg-destructive/15 text-destructive',
  cancelled: 'bg-muted text-muted-foreground',
};

/** What the web app calls each kind; `episodes` and `assets` are earlier kinds Refresh replaced. */
export const JOB_KIND_LABELS: Readonly<Record<JobKind, string>> = {
  discover: 'By category',
  walk: 'Random walk',
  refresh: 'Refresh',
  categories: 'Read categories',
  episodes: 'Check streams (old)',
  assets: 'Download files (old)',
};

function describePages(params: Job['params']): string {
  const first = typeof params['firstPage'] === 'number' ? params['firstPage'] : 1;
  const last = typeof params['maxPages'] === 'number' ? params['maxPages'] : undefined;
  if (last === undefined) {
    return first === 1 ? 'every page' : `pages ${first}–end`;
  }
  return first === last ? `page ${first}` : `pages ${first}–${last}`;
}

function describeParams(job: Job): string {
  const params = job.params;
  if (job.kind === 'discover') {
    const lists = Array.isArray(params['lists']) ? params['lists'].join(', ') : 'new';
    return `${lists} · ${describePages(params)}`;
  }
  if (job.kind === 'walk') {
    const start = typeof params['startUrl'] === 'string' ? params['startUrl'].replace(/^https?:\/\/[^/]+/, '') : 'home page';
    return `from ${start} · ${typeof params['maxPages'] === 'number' ? `up to ${params['maxPages'].toLocaleString()} new pages` : 'no limit'}`;
  }
  if (job.kind === 'refresh' && typeof params['code'] === 'string') {
    return `${params['code']}${params['checkStreams'] === true ? ' · with streams' : ''}`;
  }
  if (job.kind === 'refresh') {
    const days = (value: unknown) => (value === 0 ? 'all' : `${String(value)}+ days old`);
    const parts = [
      typeof params['pagesOlderThanDays'] === 'number' ? `pages ${days(params['pagesOlderThanDays'])}` : undefined,
      typeof params['streamsOlderThanDays'] === 'number' ? `streams ${days(params['streamsOlderThanDays'])}` : undefined,
      // Jobs from before the two thresholds.
      typeof params['olderThanDays'] === 'number' ? `pages ${days(params['olderThanDays'])}` : undefined,
    ];
    return parts.filter((part) => part !== undefined).join(' · ');
  }
  return '';
}

/** Done, running, retrying and dead as one bar, each segment labelled in the legend beside it. */
function ProgressBar(props: { readonly job: Job }) {
  const { done, claimed, pending, failed, dead, cancelled } = props.job.progress;
  const total = done + claimed + pending + failed + dead + cancelled;
  const segments = [
    { value: done, className: 'bg-primary', label: 'done' },
    { value: claimed, className: 'bg-primary/50', label: 'running' },
    { value: failed, className: 'bg-amber-500', label: 'retrying' },
    { value: dead, className: 'bg-destructive', label: 'dead' },
  ];
  return (
    <div className="space-y-1.5">
      <div className="flex h-2 gap-0.5 overflow-hidden rounded-full bg-muted" role="img" aria-label={`${done} of ${total} done`}>
        {total === 0
          ? null
          : segments.map((segment) => (segment.value === 0 ? null : <div key={segment.label} className={cn('h-full', segment.className)} style={{ width: `${(segment.value / total) * 100}%` }} />))}
      </div>
      <p className="text-xs text-muted-foreground tabular-nums">
        {done}/{total} done
        {segments
          .slice(1)
          .filter((segment) => segment.value > 0)
          .map((segment) => ` · ${segment.value} ${segment.label}`)
          .join('')}
        {pending > 0 ? ` · ${pending} waiting` : ''}
      </p>
    </div>
  );
}

function DeadItems(props: { readonly jobId: number }) {
  const query = useDeadItems(props.jobId, true);
  if (query.isPending) {
    return <LoadingRows rows={2} />;
  }
  if (query.isError) {
    return <ErrorState error={query.error} />;
  }
  if (query.data.items.length === 0) {
    return <p className="px-1 text-sm text-muted-foreground">Nothing has run out of retries.</p>;
  }
  return (
    <ul className="divide-y divide-border rounded-md border border-border text-sm">
      {query.data.items.map((item) => (
        <li key={item.itemId} className="space-y-0.5 px-3 py-2">
          <p className="truncate font-mono text-xs">
            {item.kind} {item.target}
          </p>
          <p className="text-xs text-muted-foreground">
            {item.attempts} tries · {item.lastError ?? 'no error recorded'}
          </p>
        </li>
      ))}
    </ul>
  );
}

export function JobCard(props: { readonly job: Job }) {
  const { job } = props;
  const action = useJobAction();
  const [open, setOpen] = useState(false);
  const act = (kind: JobAction) => action.mutate({ jobId: job.jobId, action: kind });
  const live = job.status === 'running' || job.status === 'pending';

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Link to={urls.job(job.jobId)} className="flex items-center gap-3 hover:underline">
          <span className="font-mono text-sm text-muted-foreground">#{job.jobId}</span>
          <span className="font-medium">{JOB_KIND_LABELS[job.kind]}</span>
        </Link>
        <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', STATUS_STYLES[job.status])}>{job.status}</span>
        <span className="text-sm text-muted-foreground">
          {[job.sourceId, describeParams(job), formatAgo(job.startedAt ?? job.createdAt)].filter((part) => part !== undefined && part !== '').join(' · ')}
        </span>
        <div className="ml-auto flex gap-1">
          {live ? (
            <Button size="sm" variant="ghost" onClick={() => act('pause')} disabled={action.isPending}>
              <Pause className="size-4" /> Pause
            </Button>
          ) : null}
          {job.status === 'paused' ? (
            <Button size="sm" variant="ghost" onClick={() => act('resume')} disabled={action.isPending}>
              <Play className="size-4" /> Resume
            </Button>
          ) : null}
          {job.progress.dead > 0 && job.status !== 'cancelled' ? (
            <Button size="sm" variant="ghost" onClick={() => act('retry')} disabled={action.isPending}>
              <RotateCcw className="size-4" /> Retry dead
            </Button>
          ) : null}
          {live || job.status === 'paused' ? (
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => act('cancel')} disabled={action.isPending}>
              <Square className="size-4" /> Cancel
            </Button>
          ) : null}
        </div>
      </div>
      <ProgressBar job={job} />
      {job.progress.dead > 0 ? (
        <div className="space-y-2">
          <button type="button" onClick={() => setOpen((value) => !value)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
            {job.progress.dead} item{job.progress.dead === 1 ? '' : 's'} ran out of retries
          </button>
          {open ? <DeadItems jobId={job.jobId} /> : null}
        </div>
      ) : null}
    </div>
  );
}

export function JobList() {
  const query = useJobs();
  if (query.isPending) {
    return <LoadingRows rows={3} />;
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (query.data.jobs.length === 0) {
    return <EmptyState title="No jobs yet" description="Start a discover job to index the newest movies." />;
  }
  return (
    <div className="space-y-3">
      {query.data.jobs.map((job) => (
        <JobCard key={job.jobId} job={job} />
      ))}
    </div>
  );
}
